import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDropzone } from 'react-dropzone';
import {
  Image,
  FileText,
  FileSpreadsheet,
  Download,
  X as XIcon,
  Loader2,
  Paperclip
} from 'lucide-react';
import type { TaskCommentAttachment } from '../../lib/types';
import { SignedUrl } from '../shared/SignedUrl';
import { SimpleAudioPlayer } from '../client-portal/SimpleAudioPlayer';
import {
  ALLOWED_COMMENT_ATTACHMENT_TYPES,
  MAX_COMMENT_ATTACHMENTS,
  MAX_COMMENT_ATTACHMENT_SIZE,
  validateCommentAttachment,
  validateAttachmentCount,
  formatFileSize,
  isImageType
} from '../../lib/utils/commentAttachmentValidation';

interface CommentAttachmentsProps {
  // For displaying existing attachments
  attachments?: TaskCommentAttachment[];
  onDelete?: (attachmentId: string) => Promise<boolean>;

  // For pre-creation mode (collecting files before comment is created)
  preCreationMode?: boolean;
  selectedFiles?: File[];
  onFilesSelected?: (files: File[]) => void;

  disabled?: boolean;
  compact?: boolean; // Smaller dropzone for inline use
}

export const CommentAttachments: React.FC<CommentAttachmentsProps> = ({
  attachments = [],
  onDelete,
  preCreationMode = false,
  selectedFiles = [],
  onFilesSelected,
  disabled = false,
  compact = false
}) => {
  const [previewFile, setPreviewFile] = useState<{ url: string; type: string } | null>(null);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return <Image className="h-4 w-4 text-blue-500" />;
    if (type.includes('pdf')) return <FileText className="h-4 w-4 text-red-500" />;
    if (type.includes('spreadsheet') || type.includes('excel')) return <FileSpreadsheet className="h-4 w-4 text-green-500" />;
    if (type.includes('word') || type.includes('document')) return <FileText className="h-4 w-4 text-blue-600" />;
    return <FileText className="h-4 w-4 text-gray-500" />;
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (disabled || !preCreationMode || !onFilesSelected) return;

    // Validate count
    const countValidation = validateAttachmentCount(selectedFiles.length, acceptedFiles.length);
    if (!countValidation.valid) {
      alert(countValidation.error);
      return;
    }

    // Validate each file
    const validFiles: File[] = [];
    for (const file of acceptedFiles) {
      const validation = validateCommentAttachment(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        alert(validation.error);
      }
    }

    if (validFiles.length > 0) {
      const newFiles = [...selectedFiles, ...validFiles].slice(0, MAX_COMMENT_ATTACHMENTS);
      onFilesSelected(newFiles);
    }
  }, [disabled, preCreationMode, onFilesSelected, selectedFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: disabled || !preCreationMode,
    noDragEventsBubbling: true, // Evita che il drop bubbli al dropzone parent (task attachments)
    maxSize: MAX_COMMENT_ATTACHMENT_SIZE,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    }
  });

  const handleRemoveSelectedFile = (fileToRemove: File) => {
    if (onFilesSelected) {
      const newFiles = selectedFiles.filter(f => f !== fileToRemove);
      onFilesSelected(newFiles);
    }
  };

  const handleDelete = async (attachmentId: string) => {
    if (disabled || !onDelete) return;

    setDeletingIds(prev => new Set(prev).add(attachmentId));
    try {
      await onDelete(attachmentId);
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(attachmentId);
        return newSet;
      });
    }
  };

  // Only show dropzone in pre-creation mode
  const showDropzone = preCreationMode && !disabled;

  // If nothing to show, return null
  if (!showDropzone && attachments.length === 0 && selectedFiles.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      {/* Compact Dropzone for pre-creation mode */}
      {showDropzone && (
        <div
          {...getRootProps()}
          className={`border border-dashed rounded-md transition-colors cursor-pointer ${
            compact ? 'p-2' : 'p-3'
          } ${
            isDragActive
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
          }`}
        >
          <input {...getInputProps()} />
          <div className={`flex items-center justify-center text-gray-500 dark:text-gray-400 ${compact ? 'text-xs' : 'text-sm'}`}>
            <Paperclip className={`mr-1 ${compact ? 'h-3 w-3' : 'h-4 w-4'}`} />
            {isDragActive ? (
              <span>Rilascia qui...</span>
            ) : (
              <span>
                Allega file {selectedFiles.length > 0 && `(${selectedFiles.length}/${MAX_COMMENT_ATTACHMENTS})`}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Selected Files (Pre-creation mode) */}
      {preCreationMode && selectedFiles.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedFiles.map((file, index) => (
            <div
              key={`selected-${index}`}
              className="flex items-center gap-1 px-2 py-1 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-xs"
            >
              {getFileIcon(file.type)}
              <span className="text-gray-700 dark:text-gray-300 max-w-[100px] truncate">{file.name}</span>
              <button
                onClick={() => handleRemoveSelectedFile(file)}
                className="text-gray-400 hover:text-red-500 transition-colors"
                title="Rimuovi"
              >
                <XIcon className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Existing Attachments */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {attachments.map((attachment) => {
            const isDeleting = deletingIds.has(attachment.id);
            const isImage = isImageType(attachment.type || '');
            const isAudio = attachment.type?.startsWith('audio/');

            if (isAudio) {
              return (
                <SignedUrl key={attachment.id} url={attachment.url}>
                  {(href) => (
                    <div className="w-full">
                      <SimpleAudioPlayer src={href ?? attachment.url} compact />
                    </div>
                  )}
                </SignedUrl>
              );
            }

            return (
              <SignedUrl key={attachment.id} url={attachment.url}>
              {(href) => (
              <div
                className={`flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs group ${
                  isImage ? 'cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600' : ''
                } ${isDeleting ? 'opacity-50' : ''}`}
                onClick={() => {
                  if (isImage) {
                    setPreviewFile({ url: href ?? attachment.url, type: attachment.type || '' });
                  }
                }}
              >
                {isDeleting ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  getFileIcon(attachment.type || '')
                )}
                <span className="text-gray-700 dark:text-gray-300 max-w-[120px] truncate">{attachment.name}</span>

                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <a
                    href={href ?? undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    onClick={(e) => e.stopPropagation()}
                    className="text-gray-400 hover:text-blue-500 transition-colors"
                    title="Scarica"
                  >
                    <Download className="h-3 w-3" />
                  </a>
                  {onDelete && !disabled && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(attachment.id);
                      }}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      title="Elimina"
                      disabled={isDeleting}
                    >
                      <XIcon className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
              )}
              </SignedUrl>
            );
          })}
        </div>
      )}

      {/* Image Lightbox */}
      {previewFile && isImageType(previewFile.type) && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99999,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setPreviewFile(null)}
        >
          <button
            onClick={() => setPreviewFile(null)}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 100000,
              padding: 12,
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '50%',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <XIcon style={{ width: 24, height: 24, color: 'white' }} />
          </button>

          <a
            href={previewFile.url}
            download
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: 16,
              right: 80,
              zIndex: 100000,
              padding: 12,
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '50%',
            }}
          >
            <Download style={{ width: 24, height: 24, color: 'white' }} />
          </a>

          <img
            src={previewFile.url}
            alt="Preview"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: 8,
            }}
          />
        </div>,
        document.body
      )}
    </div>
  );
};
